// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { DateValidationError } from '@mui/x-date-pickers';
import { clientLogger } from '@seedplanner/logger';
import {
  CROP_STATUS,
  MAX_PLANTING_WEEKLY_FREQUENCY,
  dayOfWeekToNumber,
  formatTime,
  getRecurringPlantingTimesToCreate,
  numberToDayOfWeek,
  utils,
} from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Decimal from 'decimal.js';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '../../lib/base';
import Help from '../../lib/help/Help';
import { DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import { RECURRING_PLANTINGS_QUERY } from '../../queries/RecurringPlantingQueries';
import { SURPLUSES_BUFFERS_QUERY } from '../../queries/SurplusesQueries';
import type {
  CloneRecurringPlantingDataQuery,
  CloneRecurringPlantingDataQueryVariables,
  CreateRecurringPlantingMutation,
  CreateRecurringPlantingMutationVariables,
  CropsForNewRecurringPlantingQuery,
  CropsForNewRecurringPlantingQueryVariables,
} from '../../types/__generated__/graphql';
import {
  createID,
  formatDuration,
  getID,
  getNumericID,
  getTimeRanges,
  shortenWeightUnit,
  validateNumInput,
} from '../../utils';
import { ExitDialogButton } from '../common/button';
import DialogInfoTitle from '../common/dailog/DailogInfoTitle';
import InternalDatePicker from '../common/date/InternalDatePicker';
import {
  DEFAULT_FREQUENCY_UNIT,
  EVERY_NTH_WEEKDAY_OF_MONTH,
  EVERY_X_DAY_OF_WEEK,
  type FrequencyLabel,
  daysOfTheWeek,
  frequencyLabelToInput,
  plantingFrequencyOptions,
  setFrequencyStates,
} from '../common/frequency/frequency';
import { sortCropsByName } from '../crop/sortCropsByName';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const CREATE_PLANTING_MUTATION = gql`
  mutation CreateRecurringPlanting(
    $crop_id: Int!
    $recurring_planting_name: String!
    $recurring_planting_trays: Int!
    $recurring_start_time: Int!
    $recurring_end_time: Int
    $frequency_options: RecurringOrderOptionsInput!
    $notes: String
    $create_overdue_plantings: Boolean!
  ) {
    createRecurringPlanting(
      input: {
        crop_id: $crop_id
        recurring_planting_name: $recurring_planting_name
        recurring_planting_trays: $recurring_planting_trays
        recurring_start_time: $recurring_start_time
        recurring_end_time: $recurring_end_time
        frequency_options: $frequency_options
        notes: $notes
        create_overdue_plantings: $create_overdue_plantings
      }
    ) {
      recurring_planting_created {
        id
      }
      plantings_created {
        id
      }
      tasks_created {
        id
      }
    }
  }
`;
const CROPS_AND_ORDERS_QUERY = gql`
  query CropsForNewRecurringPlanting {
    crops(filters: [{ name: "crop.status", operator: "!=", value: "archived" }]) {
      edges {
        node {
          id
          crop_name
          status
          total_grow_time
          yield_per_tray
          yield_unit
        }
      }
    }
  }
`;
const CLONE_DATA_QUERY = gql`
  query CloneRecurringPlantingData($id: ID!) {
    recurring_planting(id: $id) {
      id
      recurring_planting_name
      recurring_planting_trays
      notes
      recurring_start_time
      recurring_end_time
      recurring_options {
        frequencyGroups {
          frequencyType
          frequencies
        }
      }
      crop {
        id
      }
    }
    crops(filters: [{ name: "crop.status", operator: "!=", value: "archived" }]) {
      edges {
        node {
          id
          crop_name
          status
          total_grow_time
          yield_per_tray
          yield_unit
        }
      }
    }
  }
`;

interface Props {
  readonly cloneRecurringPlantingId?: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
}

type CropData = NonNullable<CropsForNewRecurringPlantingQuery>['crops']['edges'][number]['node'];

export default function AddRecurringPlantingDialog({ cloneRecurringPlantingId, isOpen, onClose, onSuccess }: Props) {
  const isForClone = cloneRecurringPlantingId !== undefined;
  const { timezone } = useUserContext();
  const [createPlanting, { loading: createPlantingLoading }] = useMutation<
    CreateRecurringPlantingMutation,
    CreateRecurringPlantingMutationVariables
  >(CREATE_PLANTING_MUTATION);
  const [getCropData, { loading: cropDataLoading }] = useLazyQuery<
    CropsForNewRecurringPlantingQuery,
    CropsForNewRecurringPlantingQueryVariables
  >(CROPS_AND_ORDERS_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const [getCloneData, { loading: cloneDataLoading }] = useLazyQuery<
    CloneRecurringPlantingDataQuery,
    CloneRecurringPlantingDataQueryVariables
  >(CLONE_DATA_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  const [filteredCropsData, setFilteredCropsData] = React.useState<
    NonNullable<CropsForNewRecurringPlantingQuery>['crops']['edges'][number]['node'][]
  >([]);
  const cropDataMap = React.useMemo(() => {
    const map = new Map<number, CropData>();
    for (const crop of filteredCropsData) {
      map.set(getNumericID(crop.id), crop);
    }
    return map;
  }, [filteredCropsData]);
  const filterAndSortCropsData = React.useCallback(
    (cropDataIn: NonNullable<CropsForNewRecurringPlantingQuery>['crops']) => {
      const sortedCropData = cropDataIn.edges
        .map(({ node }) => node)
        .filter((crop) => crop.status !== CROP_STATUS.ARCHIVED);
      return sortCropsByName(sortedCropData);
    },
    [],
  );

  React.useEffect(() => {
    if (isOpen) {
      if (isForClone) {
        void getCloneData({
          variables: { id: createID.recurringPlanting(cloneRecurringPlantingId) },
          onCompleted: (cloneDataIn) => {
            if (cloneDataIn?.recurring_planting != null && cloneDataIn.crops != null) {
              const readyFilteredCropsData = filterAndSortCropsData(cloneDataIn.crops);
              setFilteredCropsData(readyFilteredCropsData);

              setNotes(cloneDataIn.recurring_planting.notes ?? '');
              setName(cloneDataIn.recurring_planting.recurring_planting_name);
              setNameValidation('');
              setTrays(cloneDataIn.recurring_planting.recurring_planting_trays.toString());

              if (cloneDataIn.recurring_planting.crop != null) {
                setCropIdToUse(getNumericID(cloneDataIn.recurring_planting.crop.id));
              }

              setFrequencyStates({
                timezone,
                recurringOptions: cloneDataIn.recurring_planting.recurring_options,
                startTime: cloneDataIn.recurring_planting.recurring_start_time,
                endTime: cloneDataIn.recurring_planting.recurring_end_time,
                setFrequencyUnit,
                setFrequency,
                setDayOfWeek,
                setOccurence,
                setRecursForever,
                setStartDate,
                setEndDate,
              });
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(`Problem getting planting data: ${errorIn.message}`, {
              variant: 'error',
              autoHideDuration: 10000,
            });
          },
        });
      } else {
        void getCropData({
          onCompleted: (cropDataIn) => {
            if (cropDataIn?.crops != null) {
              const readyFilteredCropsData = filterAndSortCropsData(cropDataIn.crops);
              setFilteredCropsData(readyFilteredCropsData);
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(`Problem getting crop data: ${errorIn.message}`, {
              variant: 'error',
              autoHideDuration: 10000,
            });
          },
        });
      }
    }
  }, [isOpen, getCropData, filterAndSortCropsData, isForClone, getCloneData, cloneRecurringPlantingId, timezone]);

  const disableFields = React.useMemo(
    () => createPlantingLoading || cropDataLoading || cloneDataLoading,
    [createPlantingLoading, cropDataLoading, cloneDataLoading],
  );
  const { todayStart, sevenDaysFromTodayStart } = React.useMemo(() => getTimeRanges(timezone), [timezone]);

  const [recursForever, setRecursForever] = React.useState(true);
  const [startDate, setStartDate] = React.useState<dayjs.Dayjs | null>(utils.todayStart());
  const [endDate, setEndDate] = React.useState<dayjs.Dayjs | null>(null);
  const [startDateError, setStartDateError] = React.useState<DateValidationError | null>(null);
  // Frequency for every X weeks
  const [frequency, setFrequency] = React.useState('1');
  // Frequency type: every X weeks, every X day of the week, every nth weekday of the month
  const [frequencyUnit, setFrequencyUnit] = React.useState(DEFAULT_FREQUENCY_UNIT);
  // Day of the week for every nth weekday of the month or for every X day of the week
  const [dayOfWeek, setDayOfWeek] = React.useState(numberToDayOfWeek(dayjs().day()));
  // Occurence for every nth weekday of the month
  const [occurence, setOccurence] = React.useState('1');

  const startDateErrorMessage = React.useMemo(() => {
    if (startDateError == null) {
      return '';
    }
    switch (startDateError) {
      case 'maxDate':
        return 'Start date cannot be after end date';
      case 'minDate':
        return 'Start date cannot be before today';
      default:
        return '';
    }
  }, [startDateError]);

  const onChangeRecursForever = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRecursForever(e.target.checked);
  }, []);
  const onChangeStartDate = React.useCallback((newValue: dayjs.Dayjs | null) => {
    setStartDate(newValue);
  }, []);
  const onChangeEndDate = React.useCallback((newValue: dayjs.Dayjs | null) => {
    setEndDate(newValue);
  }, []);
  const onFrequencyUnitChange = React.useCallback((e: SelectChangeEvent<string>) => {
    setFrequencyUnit(e.target.value);
  }, []);
  const onDayOfWeekChange = React.useCallback((e: SelectChangeEvent<string>) => {
    setDayOfWeek(e.target.value);
  }, []);
  const onChangeFrequencySelect = React.useCallback((e: SelectChangeEvent<string>) => {
    setFrequency(e.target.value);
  }, []);
  const onChangeOccurence = React.useCallback((e: SelectChangeEvent<string>) => {
    setOccurence(e.target.value);
  }, []);
  dayjs.extend(utc);

  const frequencyOptionsForSubmit = React.useMemo(
    () => ({
      exceptions: [],
      frequency_groups: [
        {
          frequency_type: frequencyLabelToInput(frequencyUnit as FrequencyLabel),
          frequencies:
            frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH
              ? [dayOfWeekToNumber(dayOfWeek || 'mon'), Number.parseInt(occurence || '0')]
              : [
                  frequencyUnit === EVERY_X_DAY_OF_WEEK
                    ? dayOfWeekToNumber(dayOfWeek || 'mon')
                    : Number.parseInt(frequency || '0'),
                ],
        },
      ],
    }),
    [frequencyUnit, dayOfWeek, frequency, occurence],
  );

  const [notes, setNotes] = React.useState('');
  const [name, setName] = React.useState('');
  const [nameValidation, setNameValidation] = React.useState('');
  const [cropIdToUse, setCropIdToUse] = React.useState<number | null>(null);
  const [trays, setTrays] = React.useState('');
  const traysNumber = React.useMemo(() => Number.parseFloat(trays) || 0, [trays]);
  const [traysValidation, setTraysValidation] = React.useState('');
  const [createOverduePlantings, setCreateOverduePlantings] = React.useState(false);

  const onChangeNotes = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  }, []);
  const onChangeCreateOverduePlantings = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCreateOverduePlantings(e.target.checked);
  }, []);
  const onChangeName = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setName(e.target.value);
    if (e.target.value !== '') {
      setNameValidation('');
    }
  }, []);

  const onNameBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.value === '') {
      setNameValidation('Name is required');
    }
  }, []);
  const onChangeCropId = React.useCallback((e: SelectChangeEvent<number>) => {
    const val = e.target.value;
    const newVal = typeof val === 'string' ? Number.parseInt(val) || null : val;
    setCropIdToUse(newVal);
  }, []);
  const weightFromTrays = React.useMemo(() => {
    if (cropIdToUse == null) {
      return undefined;
    }
    const crop = cropDataMap.get(cropIdToUse);
    if (crop === undefined) {
      return undefined;
    }

    return {
      weight: Decimal.mul(crop.yield_per_tray, traysNumber).toFixed(1),
      unit: crop.yield_unit,
      yield: crop.yield_per_tray,
    };
  }, [traysNumber, cropDataMap, cropIdToUse]);

  const onChangeTrays = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTrays(e.target.value);
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTraysValidation(msg),
      validateGT: 0,
      isRequired: true,
      validateInt: true,
    });
  }, []);
  const onTraysBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTraysValidation(msg),
      validateGT: 0,
      isRequired: true,
      validateInt: true,
    });
  }, []);

  const onSubmit = React.useCallback(
    (_e?: React.MouseEvent | React.TouchEvent) => {
      void createPlanting({
        refetchQueries: [
          {
            query: DASHBOARD_QUERY,
            variables: {
              todayStart: todayStart.toString(),
              sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
            },
          },
          { query: SURPLUSES_BUFFERS_QUERY, variables: { timeAfter: todayStart } },
          {
            query: RECURRING_PLANTINGS_QUERY,
            variables: {
              todayStart: todayStart.toString(),
            },
          },
        ],
        update: (cache, { data }) => {
          const tasksCreated = (data?.createRecurringPlanting?.tasks_created?.length ?? 0) > 0;
          const plantingsCreated = (data?.createRecurringPlanting?.plantings_created?.length ?? 0) > 0;
          cache.modify({
            fields: {
              task_groups(existingGroup, { DELETE }) {
                return tasksCreated ? DELETE : existingGroup;
              },
              tasks(existingTasks, { DELETE }) {
                return tasksCreated ? DELETE : existingTasks;
              },
              harvests(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              plantings(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              taskGroup(existingGroup, { DELETE }) {
                return tasksCreated ? DELETE : existingGroup;
              },
              plantingGroup(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              plantingTimeGroup(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
            },
          });
        },
        variables: {
          recurring_planting_name: name,
          recurring_planting_trays: traysNumber,
          recurring_start_time: utils.toNoonTime(startDate ?? utils.todayStart(), timezone).unix(),
          recurring_end_time: endDate == null ? null : utils.toNoonTime(endDate, timezone).unix(),
          frequency_options: frequencyOptionsForSubmit,
          // Hacky way to make sure error comes from server instead of
          // crashing UI just in case cropSelected is somehow undefined
          crop_id: cropIdToUse ?? -1,
          notes,
          create_overdue_plantings: createOverduePlantings,
        },
        onCompleted: (dataIn) => {
          if (dataIn !== undefined && dataIn.createRecurringPlanting != null) {
            const newPlantingData = dataIn.createRecurringPlanting;
            enqueueSnackbar(`Created Planting ${getID(newPlantingData.recurring_planting_created.id)}`, {
              variant: 'success',
            });
            setName('');
            setNameValidation('');
            setCropIdToUse(null);
            setTrays('');
            setTraysValidation('');
            setNotes('');
            setStartDate(utils.todayStart(timezone));
            setEndDate(null);
            setRecursForever(true);
            setFrequency('');
            setOccurence('1');
            setDayOfWeek('');
            onSuccess(true);
          } else {
            enqueueSnackbar('Something went wrong when creating recurring planting!', {
              variant: 'error',
              autoHideDuration: 10000,
            });
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, {
            variant: 'error',
            autoHideDuration: 10000,
          });
          clientLogger.error({ title: 'new_planting_error', message: errorIn.message });
        },
      });
    },
    [
      timezone,
      createPlanting,
      cropIdToUse,
      traysNumber,
      notes,
      onSuccess,
      todayStart,
      sevenDaysFromTodayStart,
      createOverduePlantings,
      endDate,
      name,
      startDate,
      frequencyOptionsForSubmit,
    ],
  );

  const hasInputValidationError = React.useMemo(
    () => traysValidation !== '' || startDateErrorMessage !== '' || nameValidation !== '',
    [traysValidation, nameValidation, startDateErrorMessage],
  );
  const requiredFieldsFilled: boolean = React.useMemo(
    () =>
      startDate !== null &&
      cropIdToUse !== null &&
      traysNumber > 0 &&
      name !== '' &&
      (recursForever ? true : endDate !== null) &&
      (frequencyUnit === EVERY_X_DAY_OF_WEEK ? dayOfWeek !== '' : frequency !== ''),
    [startDate, cropIdToUse, traysNumber, name, recursForever, endDate, frequencyUnit, dayOfWeek, frequency],
  );
  const isSubmitDisabled: boolean = React.useMemo(
    () => !requiredFieldsFilled || hasInputValidationError || disableFields,
    [requiredFieldsFilled, hasInputValidationError, disableFields],
  );
  const onEnter = React.useCallback(() => {
    if (!isSubmitDisabled) {
      onSubmit();
    }
  }, [isSubmitDisabled, onSubmit]);

  const form = React.useMemo(
    () => (
      <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
        <TextField
          aria-describedby="planting name"
          id="planting-name"
          label="Planting Name"
          value={name}
          onChange={onChangeName}
          error={nameValidation !== ''}
          subtext={nameValidation}
          onBlur={onNameBlur}
          // onEnter={onEnter}
          hasSubtext
          required
          disabled={disableFields}
        />
        <FormControl fullWidth>
          <InputLabel id="crop-selector">Crop *</InputLabel>
          <Select
            aria-describedby="crop-selector"
            id="crop-selector"
            labelId="crop-selector"
            label="Crop *"
            value={cropIdToUse ?? ''}
            onChange={onChangeCropId}
            required={true}
            disabled={disableFields}
          >
            {filteredCropsData.map((crop) => {
              const cropLabel = `${crop.crop_name} (${formatDuration(crop.total_grow_time)})`;
              return (
                <MenuItem key={crop.id} value={getNumericID(crop.id)}>
                  {cropLabel}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <TextField
          aria-describedby="trays"
          id="trays"
          label="Trays"
          value={trays}
          numberMin={0}
          type="number"
          inputMode="numeric"
          onChange={onChangeTrays}
          onBlur={onTraysBlur}
          disabled={disableFields}
          required
          hasSubtext
          onEnter={onEnter}
          subtext={traysValidation}
          error={traysValidation !== ''}
        />
        <Box display="flex" flexDirection="column" sx={{ pl: '14px' }}>
          <FormControl required={true}>
            <FormLabel id="order-recurs-forever-label">Recur Forever</FormLabel>
            <Switch checked={recursForever} onChange={onChangeRecursForever} />
          </FormControl>
        </Box>
        <InternalDatePicker
          label={'Start Date * '}
          value={startDate}
          onChange={onChangeStartDate}
          disabled={disableFields}
          // When an existing recurring task is being edited
          // We don't want to show an error if the start date is before today- it almost always will be
          minDate={utils.todayStart(timezone)}
          maxDate={!recursForever ? (endDate ?? undefined) : undefined}
          onError={(newError) => setStartDateError(newError)}
          textFieldProps={{
            helperText: startDateErrorMessage,
          }}
        />
        {!recursForever && (
          <InternalDatePicker
            minDate={startDate ?? utils.todayStart(timezone)}
            label="End Date *"
            value={endDate}
            onChange={onChangeEndDate}
            disabled={disableFields}
          />
        )}
        <Box>
          <FormControl fullWidth>
            <InputLabel id="frequency-unit-selector">Frequency Type</InputLabel>
            <Select
              aria-describedby="frequency-unit-selector"
              id="frequency-unit-selector"
              labelId="frequency-unit-selector"
              label="Frequency Type"
              value={frequencyUnit}
              onChange={onFrequencyUnitChange}
              // onEnter={onEnter}
              required={true}
              disabled={disableFields}
            >
              {plantingFrequencyOptions.map((option) => {
                return (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
        <Box>
          {frequencyUnit === EVERY_X_DAY_OF_WEEK ? (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
              <Typography>Every</Typography>
              <FormControl fullWidth>
                <InputLabel id="frequency-day-of-week-selector">Day of Week</InputLabel>
                <Select
                  aria-describedby="frequency-day-of-week-selector"
                  id="frequency-day-of-week-selector"
                  labelId="frequency-day-of-week-selector"
                  label="Day of Week"
                  value={dayOfWeek}
                  onChange={onDayOfWeekChange}
                  // onEnter={onEnter}
                  required={true}
                  disabled={disableFields}
                >
                  {daysOfTheWeek.map((day) => {
                    return (
                      <MenuItem key={day} value={day}>
                        {day}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          ) : frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH ? (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
              <Typography>Every</Typography>
              <FormControl fullWidth>
                <InputLabel id="frequency-nth-week-of-month-selector">Occurrence</InputLabel>
                <Select
                  aria-describedby="frequency-nth-week-of-month-selector"
                  id="frequency-nth-week-of-month-selector"
                  labelId="frequency-nth-week-of-month-selector"
                  label="Occurence"
                  value={occurence}
                  onChange={onChangeOccurence}
                  // onEnter={onEnter}
                  required={true}
                  disabled={disableFields}
                >
                  {[
                    { value: '0', label: '1st' },
                    { value: '1', label: '2nd' },
                    { value: '2', label: '3rd' },
                    { value: '3', label: '4th' },
                  ].map((occ) => {
                    return (
                      <MenuItem key={occ.value} value={occ.value}>
                        {occ.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="frequency-day-of-week-selector">Day of Week</InputLabel>
                <Select
                  aria-describedby="frequency-day-of-week-selector"
                  id="frequency-day-of-week-selector"
                  labelId="frequency-day-of-week-selector"
                  label="Day of Week"
                  value={dayOfWeek}
                  onChange={onDayOfWeekChange}
                  // onEnter={onEnter}
                  required={true}
                  disabled={disableFields}
                >
                  {daysOfTheWeek.map((day) => {
                    return (
                      <MenuItem key={day} value={day}>
                        {day}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Typography>of the month</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
              <Typography>Every</Typography>
              <FormControl fullWidth>
                <InputLabel id="weekly-recurring-task-frequency">Frequency</InputLabel>
                <Select
                  aria-describedby="weekly-recurring-task-frequency"
                  id="weekly-recurring-task-frequency"
                  labelId="weekly-recurring-task-frequency"
                  label="Frequency"
                  value={frequency}
                  onChange={onChangeFrequencySelect}
                  // onEnter={onEnter}
                  required={true}
                  disabled={disableFields}
                >
                  {Array.from({ length: MAX_PLANTING_WEEKLY_FREQUENCY }, (_, i) => (i + 1).toString()).map((freq) => {
                    return (
                      <MenuItem key={freq} value={freq}>
                        {freq}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Typography>weeks</Typography>
            </Box>
          )}
        </Box>
        <TextField
          aria-describedby="planting-notes"
          id="planting-notes"
          label="Notes"
          value={notes}
          onChange={onChangeNotes}
          onEnter={onEnter}
          disabled={disableFields}
        />
        <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" justifyContent="flex-start">
          <FormControlLabel
            label="Create Overdue Plantings"
            control={<Checkbox checked={createOverduePlantings} onChange={onChangeCreateOverduePlantings} />}
          />
          <Help tooltip="By checking this, Microgreen Manager will create plantings that are too late to plant, given the harvest date and the grow time of the crop. This is useful for when you are first getting setup with Microgreen Manager. See the Tutorials page for a more detailed explanation." />
        </Box>
      </Box>
    ),
    [
      disableFields,
      onChangeNotes,
      onEnter,
      notes,
      trays,
      traysValidation,
      onChangeTrays,
      cropIdToUse,
      onChangeCropId,
      filteredCropsData,
      onTraysBlur,
      name,
      nameValidation,
      onChangeName,
      dayOfWeek,
      frequencyUnit,
      endDate,
      startDate,
      recursForever,
      timezone,
      frequency,
      onChangeEndDate,
      onChangeStartDate,
      onChangeFrequencySelect,
      onNameBlur,
      onFrequencyUnitChange,
      startDateErrorMessage,
      onDayOfWeekChange,
      onChangeRecursForever,
      occurence,
      onChangeOccurence,
      createOverduePlantings,
      onChangeCreateOverduePlantings,
    ],
  );

  const growTime: number | undefined =
    cropIdToUse != null ? (cropDataMap.get(cropIdToUse)?.total_grow_time ?? undefined) : undefined;
  const harvestTimes =
    growTime !== undefined
      ? getRecurringPlantingTimesToCreate({
          recurringStartTime: startDate?.unix() || utils.todayStart(timezone).unix(),
          recurringEndTime: recursForever ? undefined : endDate?.unix() || undefined, // endDate?.unix() can be NaN
          timezone,
          recurringOptions: {
            exceptions: [],
            frequencyGroups: [
              {
                frequencyType: frequencyLabelToInput(frequencyUnit as FrequencyLabel),
                frequencies:
                  frequencyUnit === EVERY_NTH_WEEKDAY_OF_MONTH
                    ? [dayOfWeekToNumber(dayOfWeek || 'mon'), Number.parseInt(occurence || '1')]
                    : [
                        frequencyUnit === EVERY_X_DAY_OF_WEEK
                          ? dayOfWeekToNumber(dayOfWeek || 'mon')
                          : Number.parseInt(frequency || '1'),
                      ],
              },
            ],
          },
          growTime,
          ignoreGrowTimeLimit: true,
        }).map((time) => ({
          time,
          isOverdue: time - growTime < todayStart,
          formattedTime: formatTime(time, { timezone }),
        }))
      : [];

  const overdueTimes = harvestTimes.filter((time) => time.isOverdue);
  const futureTimes = harvestTimes.filter((time) => !time.isOverdue);

  const otherInfo = React.useMemo(() => {
    const showTrayDisplay = weightFromTrays !== undefined;
    if (!showTrayDisplay) {
      return null;
    }

    return (
      <Box sx={{ marginLeft: 1, marginRight: 1, marginTop: 3 }}>
        {showTrayDisplay ? (
          <Box sx={{ mb: 1 }}>
            <DialogInfoTitle>Weight Calculation</DialogInfoTitle>
            <Typography variant="body1">
              {`${traysNumber} trays x ${weightFromTrays.yield} ${shortenWeightUnit(weightFromTrays.unit)} per tray = ${weightFromTrays.weight} ${shortenWeightUnit(weightFromTrays.unit)}`}
            </Typography>
          </Box>
        ) : null}
      </Box>
    );
  }, [weightFromTrays, traysNumber]);

  const dialogContent = React.useMemo(() => {
    return (
      <>
        {form}
        {otherInfo}
        {growTime !== undefined && (
          <Box sx={{ m: 1 }}>
            <DialogInfoTitle>Child Plantings</DialogInfoTitle>
            <Collapse in={createOverduePlantings} timeout="auto" unmountOnExit>
              <Typography>Overdue plantings will be created for these harvest dates:</Typography>
              {overdueTimes.length > 0 ? (
                <ul>
                  {overdueTimes.map(({ time }) => {
                    return <li key={time}>{formatTime(time, { timezone })}</li>;
                  })}
                </ul>
              ) : (
                <ul>
                  <li>No overdue plantings will be created.</li>
                </ul>
              )}
            </Collapse>
            <Typography>New plantings will be created for these harvest dates:</Typography>
            {futureTimes.length > 0 ? (
              <ul>
                {futureTimes.map(({ time, formattedTime }) => {
                  return <li key={time}>{formattedTime}</li>;
                })}
              </ul>
            ) : (
              <ul>
                <li>No new upcoming plantings will be created now.</li>
              </ul>
            )}
          </Box>
        )}
      </>
    );
  }, [form, growTime, overdueTimes, futureTimes, createOverduePlantings, timezone, otherInfo]);

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">New Recurring Planting</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onClose();
            }
          }}
          disabled={disableFields}
        >
          Cancel
        </Button>
        <Button
          id="submit-new-planting-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isSubmitDisabled) {
              onSubmit();
            }
          }}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
